import {useEffect, useMemo, useRef, useState} from "react";
import {Link} from "react-router-dom";
import {
  cancelStockNotification,
  getProduct,
  getProductReviews,
  setStockNotification,
  toggleWishlist,
  productImageFromUrl,
  type FieldRule,
  type ProductDetail,
  type ProductReview
} from "@zaaaz/shared";
import {DsButton, DsInput} from "@zaaaz/ui";
import {useFormValidation} from "@/lib/useFormValidation";
import {ProductCarousel} from "./ProductCarousel";
import {useI18n} from "./I18nProvider";
import {useAddToCart} from "@/hooks/useAddToCart";
import {useSession} from "./SessionProvider";
import {ProductBuyBox} from "./ProductBuyBox";
import {PdpProductHead} from "./PdpProductHead";
import {ProductCategoryTags} from "./ProductCategoryTags";
import {ProductPrice} from "./ProductPrice";
import {PdpCollapseCard} from "./PdpCollapseCard";
import {PdpDeliveryPanel} from "./PdpDeliveryPanel";
import {PdpInfoCard} from "./PdpInfoCard";
import {PdpReviewModal} from "./PdpReviewModal";
import {ShopSkeleton} from "./ShopSkeleton";
import {useDocumentTitle} from "@/hooks/useDocumentTitle";
import {openProductGallery, useFancyboxGallery} from "@/hooks/useFancyboxGallery";

const PDP_CAROUSEL_SIZE = 16;
const GENDER_SPEC_FILTER_ID = 4;

type NotifyField = "email";

const notifyRules: FieldRule<NotifyField>[] = [{field: "email", type: "email", required: true}];

export function ProductDetailView({id}: {id: number}) {
  const {t, lang} = useI18n();
  const {add, isAdding, canAddToCart} = useAddToCart();
  const {user} = useSession();
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [mainImage, setMainImage] = useState("");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyMsg, setNotifyMsg] = useState("");
  const [hasStockNotification, setHasStockNotification] = useState(false);
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewSuccessMsg, setReviewSuccessMsg] = useState("");
  const [variantFilter, setVariantFilter] = useState("");
  const [showAllVariants, setShowAllVariants] = useState(false);
  const {errors: notifyErrors, clearError: clearNotifyError, validate: validateNotify} = useFormValidation<NotifyField>();
  const skipLangFetch = useRef(true);
  useDocumentTitle(product?.nume);
  useEffect(() => {
    skipLangFetch.current = true;
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    getProduct(id).then((p) => {
      if(cancelled) return;
      setProduct(p);
      setMainImage(p.images?.[0]?.full || p.imagine);
      setHasStockNotification(!!p.hasStockNotification);
    }).catch((e: Error) => {
      if(cancelled) return;
      setProduct(null);
      setLoadError(e.message || t("Product not found"));
    }).finally(() => { if(!cancelled) setLoading(false); });
    getProductReviews(id).then((r) => { if(!cancelled) setReviews(r.items); }).catch(() => {});
    return () => { cancelled = true; };
  }, [id, t]);
  useEffect(() => {
    if(skipLangFetch.current) {
      skipLangFetch.current = false;
      return;
    }
    let cancelled = false;
    getProduct(id).then((p) => {
      if(cancelled) return;
      setProduct(p);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [lang, id]);
  useEffect(() => {
    if(!user?.email) return;
    setNotifyEmail(user.email);
  }, [user?.email]);
  const filteredVariants = useMemo(() => {
    if(!product?.variants?.length) return [];
    const q = variantFilter.trim().toLowerCase();
    if(!q) return product.variants;
    return product.variants.filter((v) => v.nume.toLowerCase().includes(q) || v.cod.toLowerCase().includes(q));
  }, [product?.variants, variantFilter]);
  const pimContent = useMemo(() => {
    if(!product) return {description: "", howToUse: "", summary: {brand: "-", category: "-", gender: "-", size: "-"}};
    const loc = lang === "ro" ? product.pimDescription?.ro : product.pimDescription?.en;
    return {
      description: loc?.description || product.descriere || "",
      howToUse: loc?.howToUse || "",
      summary: loc?.summary || {brand: "-", category: "-", gender: "-", size: "-"}
    };
  }, [product, lang]);
  const hasHowToUse = !!pimContent.howToUse.trim();
  useFancyboxGallery(!loading && !!product);
  if(loading) return <ShopSkeleton lines={6} />;
  if(loadError || !product) return <p className="notfound">{loadError || t("Product not found")}</p>;

  async function onAdd(qty = 1, e?: React.MouseEvent) {
    e?.preventDefault();
    if(!product?.inStock || !canAddToCart) return;
    try {
      await add({
        id_produs: product.id,
        _pret: product.pretUnit,
        cantitate: qty,
        _locatie: product.locatie,
        product: {
          brand: product.brand,
          nume: product.nume,
          imagine: product.imagine,
          pretEuro: product.pretEuro,
          pretLei: product.pretLei,
          pretUnit: product.pretUnit
        }
      });
    } catch(err) {
      console.error(err);
    }
  }
  async function onWishlist(e: React.MouseEvent) {
    e.preventDefault();
    if(!product) return;
    const r = await toggleWishlist(product.id);
    setProduct({...product, inWishlist: !product.inWishlist});
    window.dispatchEvent(new CustomEvent("wishlist-updated", {detail: r.count}));
  }
  async function onNotify(e: React.FormEvent) {
    e.preventDefault();
    if(!product) return;
    if(!validateNotify(notifyRules, {email: notifyEmail}, t)) return;
    try {
      const r = await setStockNotification({id: product.id, email: notifyEmail});
      setNotifyMsg(r.message || t("Notification saved"));
      setHasStockNotification(true);
      window.dispatchEvent(new CustomEvent("notifications-updated"));
    } catch(err) {
      setNotifyMsg(err instanceof Error ? err.message : t("Send failed"));
    }
  }
  async function refreshReviews() {
    try {
      const r = await getProductReviews(id);
      setReviews(r.items);
    } catch {}
  }

  async function onCancelNotify(e: React.MouseEvent) {
    e.preventDefault();
    if(!product) return;
    await cancelStockNotification(product.id);
    setHasStockNotification(false);
    setNotifyMsg("");
    window.dispatchEvent(new CustomEvent("notifications-updated"));
  }

  const hasVariants = (product.variants?.length || 0) > 1;
  const gallery = product.images?.length ? product.images : [{full: product.imagine, thumb: product.imagine}];
  const galleryUrls = gallery.map((img) => productImageFromUrl(img.full));

  function onOpenGallery(e: React.MouseEvent, imageKey = mainImage) {
    e.preventDefault();
    const idx = gallery.findIndex((img) => img.full === imageKey);
    openProductGallery(galleryUrls, idx >= 0 ? idx : 0, product!.nume).catch(() => {});
  }
  const asideSpecs = product.technicalSpecs.filter((spec) => spec.id !== GENDER_SPEC_FILTER_ID);
  const deliveryLabel = product.deliveryEstimate?.label || `${product.estimatedDelivery.dayName}, ${product.estimatedDelivery.dateShort}`;
  const summaryRows = [
    {label: t("Brand"), value: pimContent.summary.brand},
    {label: t("Category"), value: pimContent.summary.category},
    {label: t("Gender"), value: pimContent.summary.gender},
    {label: t("Size"), value: pimContent.summary.size},
    ...asideSpecs.map((spec) => ({label: spec.label, value: spec.value}))
  ];

  const detailsContent = (
    <div className="pvPdpDesc pvDetailsMain">
      {pimContent.description ? (
        <div dangerouslySetInnerHTML={{__html: pimContent.description}} />
      ) : (
        <p>{t("Coming soon...")}</p>
      )}
      {product.brandDescriere ? (
        <>
          <h3 className="pvPdpDesc__subhead">{t("About the brand:")} {product.brand}</h3>
          <div dangerouslySetInnerHTML={{__html: product.brandDescriere}} />
        </>
      ) : null}
      <ProductCategoryTags tags={product.categoryTags || []} />
      {hasHowToUse ? (
        <div className="pvPdpHowToUse">
          <h3 className="pvPdpHowToUse__label">
            <i className="fa fa-book" aria-hidden="true" />
            {t("How to use")}
          </h3>
          <div className="pvPdpHowToUse__content" dangerouslySetInnerHTML={{__html: pimContent.howToUse}} />
        </div>
      ) : null}
    </div>
  );

  const characteristicsContent = (
    <table className="pvPdpSpecTable">
      <tbody>
        {summaryRows.map((row) => (
          <tr key={row.label}>
            <th scope="row">{row.label}</th>
            <td>{row.value || "-"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const reviewsContent = (
    <div className="pvPdpReviews recenziiProdus">
      {reviewSuccessMsg ? <p className="pvPdpReviews__notice" role="status">{reviewSuccessMsg}</p> : null}
      {!reviews.length ? <p className="pvPdpReviews__empty">{t("There are no reviews for this product yet. You can be the first!")}</p> : null}
      <button type="button" className="pvPdpReviews__addBtn bx-btn bx-btn--outline" onClick={() => setReviewModalOpen(true)}>
        <i className="fa fa-commenting-o" aria-hidden="true" /> {t("Review the product")}
      </button>
      {reviews.length ? (
        <div className="recenzii clearfix pvPdpReviews__list">
          {reviews.map((r, i) => (
            <div key={i} className="recenzie" data-nota={r.stars - 1}>
              <span className="recenzie-nume">
                {r.nume}
                <span className="recenzie-data"><i className="fa fa-calendar-o" /> {r.date}</span>
              </span>
              <span className="stars">
                {Array.from({length: 5}, (_, j) => (
                  <i key={j} className={j <= r.stars - 1 ? "fa fa-star" : "fa fa-star-o"} />
                ))}
              </span>
              <div>{r.mesaj}</div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );

  const deliveryContent = <PdpDeliveryPanel deliveryLabel={deliveryLabel} t={t} />;

  const variantsContent = hasVariants ? (
    <div id="brandlineField" className={`clearfix ${showAllVariants ? "showAllVariants" : ""}`}>
      <input type="text" id="filterLines" placeholder={t("Search product variants...")} className="anim" value={variantFilter} onChange={(e) => setVariantFilter(e.target.value)} />
      <ul className="clearfix">
        {filteredVariants.map((v) => (
          <li key={v.id} className={`anim clearfix ${v.active ? "active" : ""}`}>
            <Link to={v.url} title={v.nume} className="anim clearfix">
              <span className="imgHolder">
                <img src={productImageFromUrl(v.imagine)} alt={v.nume} />
              </span>
              <span className="cal rl">
                {v.nume}
                <span className="cod"><i className="fa fa-barcode" /> {v.cod}</span>
                <ProductPrice pretEuro={v.pretEuro} pretLei={v.pretLei} mainClass="lei" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {filteredVariants.length > 6 ? (
        <a href="#" id="showAllL" className="anim" onClick={(e) => { e.preventDefault(); setShowAllVariants(!showAllVariants); }}>
          <i className={`fa ${showAllVariants ? "fa-minus-circle" : "fa-plus-circle"}`} /> {t("Show all")}
        </a>
      ) : null}
    </div>
  ) : null;

  return (
    <>
      <div className="wrapper clearfix pvBxLayout">
        <div className="inside">
          <div className="pvPdpPage">
            <div className="pvPdpLeft">
              <nav className="pvBreadcrumbClamp pvBreadcrumbClamp--pdpLeft" aria-label="Breadcrumb">
                <ol className="pvBreadcrumbClamp__list">
                  {product.breadcrumb.filter((b) => b.label).map((b, i, items) => (
                    <li key={`${b.url}-${i}`} className="pvBreadcrumbClamp__item">
                      {i > 0 ? <span className="pvBreadcrumbClamp__sep" aria-hidden="true">/</span> : null}
                      {i === items.length - 1 ? (
                        <span className="pvBreadcrumbClamp__current" aria-current="page">{b.label}</span>
                      ) : (
                        <Link to={b.url} className="pvBreadcrumbClamp__link">{b.label}</Link>
                      )}
                    </li>
                  ))}
                </ol>
              </nav>
              <header className="pvPdpMobileHead">
                <p className="pvPdpMobileHead__brand">
                  <Link to={product.brandUrl}>{product.brand}</Link>
                </p>
                <h1 className="pvPdpMobileHead__title">{product.nume}</h1>
                <p className="pvPdpMobileHead__meta">
                  <span className="pvPdpMobileHead__id">{t("ID #")} {product.id}</span>
                  {product.cod ? (
                    <span className="pvPdpMobileHead__ean">{t("EAN")} {product.cod}</span>
                  ) : null}
                </p>
              </header>
              <div className="cc c1 galerie pvGallery pvPdpGallery clearfix">
              {!gallery.length || !gallery[0].full ? (
                <img src={productImageFromUrl("")} style={{width: "40%", margin: "24px auto 0 auto", display: "block"}} className="main" alt={product.nume} />
              ) : (
                <>
                  <div id="mainImage">
                    {product.freeShipping ? <i><i className="fa fa-truck" /> {t("Free")}</i> : null}
                    <a
                      href={productImageFromUrl(mainImage)}
                      className="anim fbox"
                      title={product.nume}
                      onClick={(e) => onOpenGallery(e)}
                    >
                      <img src={productImageFromUrl(mainImage)} alt={product.nume} />
                    </a>
                  </div>
                  {gallery.length > 1 ? (
                    <div id="thumbs">
                      {gallery.map((img, i) => (
                        <a key={i} href="#" className={`anim ${mainImage === img.full ? "active" : ""}`} onClick={(e) => { e.preventDefault(); setMainImage(img.full); }}>
                          <img src={productImageFromUrl(img.thumb)} className="anim" alt={product.nume} />
                        </a>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
              </div>
            </div>
            <aside className="cc pvProductAside infoProdus">
              <div className="pvProductAsideSticky">
                <div className="pvProductAsideCard pvProductAsideCard--main">
                  <PdpProductHead
                    brand={product.brand}
                    brandUrl={product.brandUrl}
                    title={product.nume}
                    reviewStars={product.reviewStars}
                    reviewCount={product.reviewCount}
                    sku={product.cod}
                    inWishlist={product.inWishlist}
                    onWishlist={onWishlist}
                  />
                  {product.inStock ? (
                    <ProductBuyBox
                      product={product}
                      onAdd={onAdd}
                      isAdding={isAdding(product.id)}
                      canAddToCart={canAddToCart}
                      onWishlist={onWishlist}
                    />
                  ) : (
                    <div className="clearfix priceTable pvBuyWrap bx-card bx-buyBox--notify" style={{padding: 20}}>
                      <h3 className="bx-h2">{t("Stock notification")}</h3>
                      {hasStockNotification ? (
                        <>
                          <p>{t("You have an active stock notification for this product!")}</p>
                          <DsButton variant="outline" onClick={onCancelNotify}><i className="fa fa-times" /> {t("Cancel notification")}</DsButton>
                        </>
                      ) : (
                        <>
                          <p>{t("Be the first to find out when it returns to stock!")}</p>
                          <form action="#" className="form clearfix" id="notifStock" onSubmit={onNotify} noValidate>
                            <input type="hidden" name="id" value={product.id} />
                            <DsInput
                              type="email"
                              name="email"
                              value={notifyEmail}
                              onChange={(e) => { setNotifyEmail(e.target.value); clearNotifyError("email"); }}
                              placeholder={t("E-mail address")}
                              error={notifyErrors.email}
                            />
                            <div style={{marginTop: 12}}>
                              <DsButton type="submit" variant="primary">{t("Notify me")}</DsButton>
                            </div>
                          </form>
                        </>
                      )}
                      {notifyMsg ? <p><small>{notifyMsg}</small></p> : null}
                    </div>
                  )}
                  {product.isTester ? (
                    <div className="pvTesterNote">{t("This product is a tester and it might be shipped unboxed.")}</div>
                  ) : null}
                </div>
              </div>
            </aside>
            <div className="pvPdpRest">
              <div className="pvPdpInfoStack">
                <PdpInfoCard icon="fa-file-text-o" title={t("Product details")}>
                  {detailsContent}
                </PdpInfoCard>
                {summaryRows.length ? (
                  <PdpInfoCard icon="fa-info-circle" title={t("Characteristics")}>
                    {characteristicsContent}
                  </PdpInfoCard>
                ) : null}
                <PdpInfoCard icon="fa-commenting-o" title={t("Reviews")}>
                  {reviewsContent}
                </PdpInfoCard>
                <PdpCollapseCard icon="fa-truck" title={t("Delivery and returns")}>
                  {deliveryContent}
                </PdpCollapseCard>
                {variantsContent ? (
                  <PdpCollapseCard icon="fa-th-large" title={t("Product variants")}>
                    {variantsContent}
                  </PdpCollapseCard>
                ) : null}
              </div>
              {product.recommended?.length ? (
                <section className="bx-homeProducts">
                  <div className="bx-homeProducts__head">
                    <h2 className="bx-sectionTitle">{t("Recommended products")}</h2>
                  </div>
                  <ProductCarousel products={product.recommended.slice(0, PDP_CAROUSEL_SIZE)} visibleCount={3} />
                </section>
              ) : null}
              {product.recentlyViewed?.length ? (
                <section className="bx-homeProducts">
                  <div className="bx-homeProducts__head">
                    <h2 className="bx-sectionTitle">{t("RECENTLY VIEWED")}</h2>
                  </div>
                  <ProductCarousel products={product.recentlyViewed.slice(0, PDP_CAROUSEL_SIZE)} visibleCount={3} />
                </section>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <PdpReviewModal
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        productId={product.id}
        defaultNume={user?.nume || ""}
        defaultEmail={user?.email || ""}
        onSubmitted={() => {
          setReviewSuccessMsg(t("The review has been sent!"));
          refreshReviews();
        }}
      />
    </>
  );
}
